/* @flow */

import * as React from 'react';
import { without } from 'lodash';
import { TransitionGroup } from 'react-transition-group-fork';
import AnimatedGroupChild from './AnimatedGroupChild';
import AnimatedGroupChildEmpty from './AnimatedGroupChildEmpty';

type ContextOptions = {
  anyLeaving: boolean,
  anyEntering: boolean
};

type Props = {
  children?: any,
  component?: any,
  onAppear?: boolean | () => (node: any, contextOptions: ContextOptions) => TimelineMax,
  onEnter?: boolean | () => (node: any, contextOptions: ContextOptions) => TimelineMax,
  onLeave?: boolean | () => (node: any, contextOptions: ContextOptions) => TimelineMax
};

type State = {
  leavingKeys: Set<string>,
  enteringKeys: Set<string>
};
/**
 * Animate a group of children when they appear/leave or enter
 * This hooks into the normal react events and allows us to play a timeline at that point
 * */
class AnimatedGroup extends React.Component {
  props: Props;

  state: State = {
    enteringKeys: new Set(),
    leavingKeys: new Set()
  };

  static defaultProps = {
    children: undefined,
    component: 'div',
    onAppear: undefined,
    onEnter: undefined,
    onLeave: undefined
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { children } = this.props;
    const keyPicker = cd =>
      React.Children.toArray(cd)
        .filter(child => child)
        .map(child => child.key);
    const oldKeys = keyPicker(children);
    const newKeys = keyPicker(nextProps.children);

    this.setState({
      enteringKeys: new Set(without(newKeys, ...oldKeys)),
      leavingKeys: new Set(without(oldKeys, ...newKeys))
    });
  }

  wrapChild = (child: any) => {
    const { enteringKeys, leavingKeys } = this.state;
    const commonProps = {
      anyEntering: enteringKeys.size > 0,
      anyLeaving: leavingKeys.size > 0,
      isEntering: enteringKeys.has(child.key),
      isLeaving: leavingKeys.has(child.key),
      key: child.key
    };

    if (child.type === AnimatedGroupChildEmpty) {
      return child;
    }

    if (child.type === AnimatedGroupChild) {
      // if you want individual animations, use an animated group child
      return React.cloneElement(child, {
        ...child.props,
        ...commonProps
      });
    }

    const { onAppear, onEnter, onLeave } = this.props;

    if (!onAppear && !onEnter && !onLeave) {
      // full blown animated child can possibly be a bit more expensive than necessary
      // so if there's no animations specified, just wrap in a very
      // simple wrapper that just returns the child
      // we need to ensure we wrap it as functional components will not have the
      // lifecycle events and could cause an error
      return <AnimatedGroupChildEmpty>{child}</AnimatedGroupChildEmpty>;
    }

    return (
      <AnimatedGroupChild {...commonProps} onAppear={onAppear} onEnter={onEnter} onLeave={onLeave}>
        {child}
      </AnimatedGroupChild>
    );
  };

  render() {
    const { onAppear, onEnter, onLeave, ...otherProps } = this.props;
    return <TransitionGroup {...otherProps} childFactory={child => this.wrapChild(child)} />;
  }
}

export default AnimatedGroup;
